"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Heading, SearchDropdown, SearchField, Select } from "ui-components";

import { IOrganizationsSearchParams } from "@/src/app/[locale]/(public)/organizations/page";
import CorporateSolutionsLeadForm from "@/src/components/organization/CorporateSolutionsLeadForm";
import {
  IndustryEntity,
  useOrganizationGetOrganizationMetaData,
} from "@/src/hooks/api/generated";
import { useNavigate } from "@/src/hooks/useNavigate";

import { Filters } from "../enums/filters.enum";

const SEARCH_DEBOUNCE_MS = 300;

interface IOrganizationListingHeader {
  searchParams: IOrganizationsSearchParams;
  isLoading: boolean;
}

export function OrganizationListingHeader({
  searchParams,
  isLoading,
}: Readonly<IOrganizationListingHeader>): JSX.Element {
  const router = useRouter();
  const { pushQueryParam } = useNavigate();
  const { data } = useOrganizationGetOrganizationMetaData();

  const urlSearch = searchParams[Filters.search] ?? "";
  const [searchValue, setSearchValue] = useState(urlSearch);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Keep the input in sync when the URL search changes elsewhere (Clear
  // filters, browser back/forward) without fighting the user's typing.
  useEffect(() => {
    setSearchValue(urlSearch);
  }, [urlSearch]);

  // Clean up any pending debounce on unmount.
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  // Debounce URL updates (300ms) and use router.replace so typing doesn't
  // stack history entries.
  const handleSearchChange = (term: string): void => {
    setSearchValue(term);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const qs = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value && key !== Filters.search && key !== Filters.page) {
          qs.set(key, value);
        }
      });
      if (term) {
        qs.set(Filters.search, term);
      }
      qs.set(Filters.page, "1");
      const query = qs.toString();
      router.replace(query ? `/organizations?${query}` : "/organizations", {
        scroll: false,
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className="flex flex-col items-center justify-center flex-1 w-full mt-10 gap-4 md:mt-4 md:gap-[30px]">
      <div className="flex flex-col w-full gap-x-5 items-center justify-center">
        <div className="flex justify-center lg:justify-end w-full">
          <CorporateSolutionsLeadForm
            intent="secondary"
            triggerButtonText="Become a corporate member"
          />
        </div>
        <Heading variant="h1">Organizations</Heading>
      </div>
      <div className="flex flex-col gap-y-5">
        <SearchField
          placeholder="Search for an organization"
          size="xxl"
          className="lg:w-[970px]"
          value={searchValue}
          onChange={handleSearchChange}
          isLoading={isLoading}
          isSearchButtonVisible={false}
        />
        <div className="flex gap-4 flex-col md:flex-row w-full justify-end items-center">
          <SearchDropdown
            placeholder="Industry"
            size="lg"
            options={data?.industries.map((industry: IndustryEntity) => ({
              id: industry.id,
              name: industry.name,
            }))}
            selectedKey={searchParams[Filters.industry] ?? null}
            onSelectionChange={(selectedValue) => {
              pushQueryParam({
                key: Filters.industry,
                value: selectedValue?.toString(),
                shouldResetPage: true,
              });
            }}
          />
          <Select
            options={[
              {
                id: "NO",
                title: "No",
              },
              {
                id: "ASC",
                title: "A-Z",
              },
              {
                id: "DESC",
                title: "Z-A",
              },
            ]}
            size="sm"
            className="!w-72 md:!w-[155px] "
            placeholder="Sort by"
            value={searchParams[Filters.sortBy] ?? null}
            onSelection={(selectedValue) => {
              pushQueryParam({
                key: Filters.sortBy,
                value: selectedValue === "NO" ? "" : selectedValue?.toString(),
                shouldResetPage: true,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}
